import React from 'react'
import Grid from '../../../common/layout/grid'

export const GALE_TYPES = [
  { id: 'martingale', label: 'Martingale (dobra no sinal)' },
  { id: 'linear', label: 'Linear (3, 6, 9, 12)' },
  { id: 'flat', label: 'Fixo' },
  { id: 'soros', label: 'Soros (reinveste ganho)' },
  { id: 'soros_gale', label: 'Soros gale (½ perda contínua)' },
]

export const DEFAULT_GALE_TYPE = 'martingale'

export function normalizeGaleType(value) {
  const type = String(value || '').toLowerCase().trim()
  if (type === 'soros_recovery') return 'soros_gale'
  return GALE_TYPES.some((opt) => opt.id === type) ? type : DEFAULT_GALE_TYPE
}

/**
 * Aceita [10], [{crash_prompt_id, gale_type}] ou o JSON string do banco.
 */
export function parseBotPromptBindings(raw) {
  let value = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch (e) {
      return []
    }
  }
  if (!Array.isArray(value)) return []

  const byId = new Map()
  value.forEach((item) => {
    let id = 0
    let galeType = DEFAULT_GALE_TYPE
    if (item && typeof item === 'object') {
      id = Number(item.crash_prompt_id != null ? item.crash_prompt_id : item.id)
      galeType = normalizeGaleType(item.gale_type)
    } else {
      id = Number(item)
    }
    if (!Number.isFinite(id) || id <= 0) return
    byId.set(id, { crash_prompt_id: id, gale_type: galeType })
  })
  return Array.from(byId.values())
}

/**
 * Checkbox por prompt + select de gale nas linhas marcadas.
 * Usado na aba Bots com um bot selecionado.
 */
export default function BotPromptGaleSelect({
  label,
  options = [],
  value = [],
  galeByPrompt = {},
  onChange,
  cols = '12',
  emptyLabel = 'Nenhum prompt cadastrado',
  disabled = false,
}) {
  const selected = (Array.isArray(value) ? value : [])
    .map(Number)
    .filter((id) => Number.isFinite(id) && id > 0)
  const selectedSet = new Set(selected)
  const byId = new Map(options.map((opt) => [Number(opt.id), opt]))

  const emit = (nextIds, nextGales) => {
    if (disabled || !onChange) return
    onChange({
      promptIds: nextIds,
      galeByPrompt: nextGales,
      bindings: nextIds.map((id) => ({
        crash_prompt_id: id,
        gale_type: normalizeGaleType(nextGales[id]),
      })),
    })
  }

  const toggle = (id) => {
    if (disabled) return
    if (selectedSet.has(id)) {
      const nextIds = selected.filter((x) => x !== id)
      const nextGales = { ...galeByPrompt }
      delete nextGales[id]
      emit(nextIds, nextGales)
      return
    }
    emit(selected.concat(id), {
      ...galeByPrompt,
      [id]: normalizeGaleType(galeByPrompt[id]),
    })
  }

  const changeGale = (id, galeType) => {
    if (disabled || !selectedSet.has(id)) return
    emit(selected, { ...galeByPrompt, [id]: normalizeGaleType(galeType) })
  }

  const optionLabel = (opt, id) => {
    if (!opt) return `#${id}`
    const name = opt.name || opt.title || `#${id}`
    const detail = opt.detail
      || (opt.min_result != null && Number.isFinite(Number(opt.min_result))
        ? `≥ ${Number(opt.min_result)}x`
        : null)
    return detail ? `${name} (${detail})` : name
  }

  return (
    <Grid cols={cols}>
      <div className='form-group' style={{ padding: '0 15px' }} data-testid='bot-prompt-gale'>
        {label ? <label style={{ fontWeight: 600 }}>{label}</label> : null}

        <div
          style={{
            maxHeight: 240,
            overflowY: 'auto',
            border: '1px solid #ddd',
            borderRadius: 4,
            padding: '6px 10px',
            background: disabled ? '#f5f5f5' : '#fff',
          }}
        >
          {options.length === 0 ? (
            <div style={{ color: '#9e9e9e', fontSize: 13 }}>{emptyLabel}</div>
          ) : (
            options.map((opt) => {
              const id = Number(opt.id)
              const name = optionLabel(opt, id)
              const checked = selectedSet.has(id)
              const galeType = normalizeGaleType(galeByPrompt[id])
              return (
                <div
                  key={id}
                  data-testid={`bot-prompt-row-${id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    margin: '6px 0',
                    flexWrap: 'wrap',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      margin: 0,
                      cursor: disabled ? 'default' : 'pointer',
                      fontWeight: 400,
                      fontSize: 13,
                      flex: '1 1 180px',
                    }}
                  >
                    <input
                      type='checkbox'
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(id)}
                      aria-label={name}
                    />
                    <span>{name}</span>
                  </label>
                  {checked ? (
                    <select
                      className='form-control input-sm'
                      style={{ width: 220, height: 30, padding: '2px 8px', fontSize: 12 }}
                      value={galeType}
                      disabled={disabled}
                      aria-label={`Tipo de gale do prompt ${name}`}
                      data-testid={`gale-type-${id}`}
                      onChange={(e) => changeGale(id, e.target.value)}
                    >
                      {GALE_TYPES.map((row) => (
                        <option key={row.id} value={row.id}>{row.label}</option>
                      ))}
                    </select>
                  ) : null}
                </div>
              )
            })
          )}
        </div>
      </div>
    </Grid>
  )
}
